import { put, takeLatest, select, call, all } from 'redux-saga/effects';
import {
  HOME_FETCH_CASES_BEGIN,
  HOME_FETCH_CASES_SUCCESS,
  HOME_FETCH_CASES_FAILURE,
  HOME_FETCH_CASES_DISMISS_FEEDBACK,
  HOME_FETCH_CASES_SUCCESS_DUMMY,
  HOME_TEMPLATE_FILTER_NONE,
} from './constants';
import { AUTH_LOGIN } from 'features/auth/redux/constants';
import api from 'common/api';
import { selectCases, selectAcceptTsAndCsSuccess, selectIsNewCase } from 'common/selectors';

const authLoginPattern = (action) => action && action.type === AUTH_LOGIN && !action.$isSync;

export function fetchCases() {
  // If need to pass args to saga, pass it with the begin action.
  return {
    type: HOME_FETCH_CASES_BEGIN,
  };
}

export function dismissFetchCasesFeedback() {
  return {
    type: HOME_FETCH_CASES_DISMISS_FEEDBACK,
  };
}

// worker Saga: will be fired on CASE_GET_USER_DETAILS_BEGIN actions
export function* doFetchCases(action) {
  const isAuthLogin = action.type === AUTH_LOGIN;

  // CRITICAL FIX: Ignore AUTH_LOGIN actions that are synced from other tabs
  // redux-state-sync marks synced actions with $isSync: true
  // This prevents duplicate /users-me calls when multiple tabs are open
  if (isAuthLogin && action.$isSync) {
    // This AUTH_LOGIN came from another tab via redux-state-sync
    // Don't fetch cases in this tab - let the originating tab handle it
    return;
  }

  const acceptTsAndCsSuccess = yield select(selectAcceptTsAndCsSuccess);
  const isNewCase = yield select(selectIsNewCase);
  const cases = acceptTsAndCsSuccess || isNewCase ? [] : yield select(selectCases);

  // don't fetch cases if we have them already,
  // but still trigger success action so hub connectionId can be loaded
  if (cases.length > 0) {
    return yield put({
      type: HOME_FETCH_CASES_SUCCESS_DUMMY,
    });
  }

  // If we've already successfully fetched cases once in this session,
  // avoid hitting /users-me again on subsequent AUTH_LOGIN events, even
  // when the user has zero cases.
  const { fetchCasesPending, fetchCasesFailure } = yield select((state) => state.home || {});
  const alreadyFetchedOnce =
    !fetchCasesPending && !fetchCasesFailure && !acceptTsAndCsSuccess && !isNewCase;

  if (isAuthLogin && alreadyFetchedOnce) {
    return yield put({
      type: HOME_FETCH_CASES_SUCCESS_DUMMY,
    });
  }

  let res = yield call(api.get, `/users-me`);

  if (res && res.error) {
    yield put({
      type: HOME_FETCH_CASES_FAILURE,
      feedback: { message: 'feedback.fetchCasesFailure', error: res.error, retryAction: action },
    });
    return;
  }
  yield put({
    type: HOME_FETCH_CASES_SUCCESS,
    data: res,
  });
}
/*
  Alternatively you may use takeEvery.

  takeLatest does not allow concurrent requests. If an action gets
  dispatched while another is already pending, that pending one is cancelled
  and only the latest one will be run.
*/
export function* watchFetchCases() {
  yield all([
    takeLatest(HOME_FETCH_CASES_BEGIN, doFetchCases),
    takeLatest(authLoginPattern, doFetchCases),
  ]);
}

// Redux reducer
export function reducer(state, action) {
  switch (action.type) {
    case HOME_FETCH_CASES_BEGIN:
      return {
        ...state,
        fetchCasesPending: true,
        fetchCasesFeedback: null,
      };

    case HOME_FETCH_CASES_SUCCESS: {
      const cases = action.data.cases && Array.isArray(action.data.cases) ? action.data.cases : [];
      const mappedCases = cases.map(
        ({
          id,
          name,
          claimNumber,
          permissions,
          userNeedsToAcceptTsAndCs,
          showTranscript,
          showTranscriptPrivate,
          useNewRTViewer,
          fromTemplate,
          stats = null,
        }) => ({
          id,
          name,
          claimNumber,
          userNeedsToAcceptTsAndCs,
          showTranscript,
          showTranscriptPrivate,
          useNewRTViewer,
          fromTemplate,
          stats: stats && typeof stats === 'object' ? stats : null,
          canViewRealTime:
            permissions && permissions.hearingRoom && permissions.hearingRoom.canViewRealTime,
          canBrowseDocuments: permissions && permissions.documents && permissions.documents.list,
          canJoinConference: (
            (permissions && permissions.hearingRoom && permissions.hearingRoom.join) ||
            []
          ).includes('public'),
          canViewDocuments: (
            (permissions && permissions.hearingRoom && permissions.hearingRoom.viewDocuments) ||
            []
          ).includes('public'),
          canStream: (
            (permissions && permissions.hearingRoom && permissions.hearingRoom.stream) ||
            []
          ).includes('public'),
          canJoinConferencePrivate: (
            (permissions && permissions.hearingRoom && permissions.hearingRoom.join) ||
            []
          ).includes('private'),
          canViewDocumentsPrivate: (
            (permissions && permissions.hearingRoom && permissions.hearingRoom.viewDocuments) ||
            []
          ).includes('private'),
          canStreamPrivate: (
            (permissions && permissions.hearingRoom && permissions.hearingRoom.stream) ||
            []
          ).includes('private'),
          canViewTeamAdmin: permissions && permissions.admin && permissions.admin.team,
          canViewCaseAdmin: permissions && permissions.admin && permissions.admin.caseAdmin,
          canViewModerator: permissions && permissions.admin && permissions.admin.moderator,
        }),
      );

      // Build unique template list for system admins
      const templateSet = new Set();
      let hasNoTemplate = false;

      mappedCases.forEach(({ fromTemplate }) => {
        if (fromTemplate && typeof fromTemplate === 'string' && fromTemplate.trim()) {
          templateSet.add(fromTemplate.trim());
        } else {
          hasNoTemplate = true;
        }
      });

      const caseTemplates = Array.from(templateSet)
        .sort()
        .map((template) => ({
          value: template,
          label: template,
        }));

      if (hasNoTemplate) {
        caseTemplates.unshift({
          value: HOME_TEMPLATE_FILTER_NONE,
          label: 'No template',
        });
      }

      // Extract system permissions for filtering
      const systemPermissions = action.data.systemPermissions || {};
      const adminPermissions = systemPermissions.admin || {};
      const filterCasesByTemplate = adminPermissions.filterCasesByTemplate || false;

      return {
        ...state,
        fetchCasesPending: false,
        fetchCasesFailure: false,
        fetchCasesFeedback: action.feedback,
        cases: mappedCases,
        caseTemplates,
        filterCasesByTemplate,
      };
    }

    case HOME_FETCH_CASES_SUCCESS_DUMMY:
      return {
        ...state,
        fetchCasesPending: false,
        fetchCasesFailure: false,
      };

    case HOME_FETCH_CASES_FAILURE:
      return {
        ...state,
        fetchCasesPending: false,
        fetchCasesFailure: true,
        fetchCasesFeedback: action.feedback,
      };

    case HOME_FETCH_CASES_DISMISS_FEEDBACK:
      return {
        ...state,
        fetchCasesFeedback: null,
      };

    default:
      return state;
  }
}
